import { combineLatest } from 'rxjs'
import { map as rxMap } from 'rxjs/operators'
import 'whatwg-fetch'
import Route from 'route-parser'
import {
  isArray,
  reduce,
  result,
  map,
  find,
  range,
  first,
  last,
  dropWhile,
  groupBy,
  sortBy,
  some,
  startsWith,
  filter
} from 'libraries/lodash'
import dayjs from 'dayjs'
import objectSupport from 'dayjs/plugin/objectSupport'
import { mapResponse } from 'front-end-core/helpers/dataflow'
import {
  MW_EVENT_TYPES,
  MW_FEATURED_DEAL,
  MW_STATS_SUMMARY,
  MW_LATEST_DEALS,
  MW_EVENT_CONFIGURATION,
  MW_SHARED_EVENTS_CONFIGURATION,
  MW_LIST_DEALS,
  MW_STATS,
  MW_DEAL
} from 'constants/requests'
import dealTypes from 'constants/mw-deal-types'
import colors from 'constants/mw-analytics-colours'
import { fetch as fetchService } from 'services/ApiService'
import { URL_MW_DEAL_DOCUMENT } from 'constants/urls'
import { DATE_FORMAT } from 'constants/locale'

dayjs.extend(objectSupport)

export const requestDeal = ({ dealId }) => fetchService(MW_DEAL, { dealId })

export const requestDeals = dealIds => combineLatest(
  dealIds.map((dealId) => requestDeal({ dealId }))
)

export const requestDealDocument = ({ dealId, docId }) => {
  const url = new Route(URL_MW_DEAL_DOCUMENT).reverse({ dealId, docId })
  return fetch(url)
    .then(response => {
      if (!response.ok) {
        throw new Error('Error: Unable to open document')
      }
      const buffer = response.arrayBuffer()
      return buffer
    })
}

export const requestFeaturedDeal = () => fetchService(MW_FEATURED_DEAL)

export const requestLatestDeals = () => fetchService(MW_LATEST_DEALS)

export const requestEventTypes = () => fetchService(MW_EVENT_TYPES)

export const requestStatsSummary = () => fetchService(MW_STATS_SUMMARY)

export const requestEventConfiguration = ({ type }) => fetchService(MW_EVENT_CONFIGURATION, { type })

export const requestSharedEventsConfiguration = () => fetchService(MW_SHARED_EVENTS_CONFIGURATION)

export const requestListDeals = ({ eventType, page, pageSize, parameters, searchPhrase }) => (
  fetchService(MW_LIST_DEALS, {}, {
    ...eventType !== 'all' && { eventType },
    query: searchPhrase,
    page,
    pageSize,
    parameters
  })
)

export const requestStats = ({ parameterName, interval, groupBy }) => fetchService(MW_STATS, {}, {
  parameterName,
  interval,
  groupBy
})

const normalizeDataset = (data, normalize = false) => {
  if (!normalize) {
    return data
  }
  return mapParam(
    'plot',
    mapParam(
      'y',
      (y, index) => y / (data.reduce((a, { plot }) => a + plot[index].y, 0) || 1)
    )
  )(data)
}

const convertFromDate = (yearParts) => (date) => {
  const d = dayjs(date)

  return d.year() * yearParts + Math.floor(d.month() * yearParts / 12)
}

const convertToDate = (yearParts) => (int) => dayjs({
  years: Math.floor(int / yearParts),
  months: Math.floor(int % yearParts) * (12 / yearParts),
  date: 1
})

const mapParam = (param, map) => (arr) => arr.map((r, i) => ({ ...r, [param]: map(r[param], i) }))

const getColor = (index) => colors[index % colors.length]

const getPropName = prop => isArray(prop) ? prop[0] : prop

const getPropLabel = prop => isArray(prop) ? prop[1] : prop

const getPropOptions = prop => isArray(prop) ? prop[2] || {} : {}

const getDateSpan = (results, interval, plot) => {
  const yearParts = interval === 'y'
    ? 1
    : interval === 'q'
      ? 4
      : 12

  const dates = Object.keys(results).sort().map(date => ({ i: convertFromDate(yearParts)(date), date, hasValue: plot ? plot.reduce((a, prop) => a || !!results[date][prop], false) : true }))
  const datesCalibrated = dropWhile(dates, d => !d.hasValue)
  const datesObj = Object.assign({}, ...datesCalibrated.map(({ i, date }) => ({ [i]: date })))
  return datesCalibrated.length
    ? range(first(datesCalibrated).i, last(datesCalibrated).i + 1).map(i => ({ i, sourceDate: datesObj[i], date: convertToDate(yearParts)(i) }))
    : []
}

const getPlotData = ({ plot, results, interval, masterDateSpan = null, factor }) => {
  const dateFormat = interval === 'y'
    ? 'YYYY'
    : interval === 'q'
      ? '[Q]Q \'YY'
      : 'MMM YY'

  const dateSpan = masterDateSpan || getDateSpan(results, interval, plot.map(getPropName))
  const returnData = plot.map((prop, index) => ({
    name: getPropName(prop),
    label: getPropLabel(prop),
    options: getPropOptions(prop),
    color: getColor(index),
    plot: dateSpan.map(({ sourceDate, date }, index) => ({
      x: date.format(dateFormat),
      y: result(results, [sourceDate, getPropName(prop)], 0) * factor,
      range: [date.format(DATE_FORMAT), result(dateSpan, [index + 1, 'date'], dayjs()).clone().subtract(1, 'day').format(DATE_FORMAT)]
    }))
  }))
  return returnData
}

export const getStats = ({ parameterName }) => requestStats({ parameterName }).pipe(
  rxMap((mapResponse(response => (
    Object.assign({}, ...response.map(param => ({ [param.parameterName]: param })))
  ))))
)

export const getGraphStats = ({
  parameterName,
  interval,
  plot = ['average'],
  factor = 1,
  normalize = false
}) => requestStats({ parameterName, interval }).pipe(
  rxMap((mapResponse(response => {
    const dataEntry = find(response, r => r.parameterName === parameterName)
    const { type, results } = dataEntry

    return {
      displayName: parameterName,
      data: type === 'EmptyResults'
        ? []
        : normalizeDataset(
          getPlotData({ plot, results, interval, factor }),
          normalize
        )
    }
  })))
)

export const getGroupedStats = ({
  parameterName,
  groupBy,
  interval,
  aggregate = 'count',
  reorder = null,
  displayName,
  factor = 1,
  normalize = false
}) => requestStats({ parameterName, groupBy, interval }).pipe(
  rxMap((mapResponse(response => {
    const { results } = find(response, r => r.parameterName === parameterName)
    const masterDateSpan = getDateSpan(reduce(results, (a, r) => Object.assign({}, r, a), {}), interval)
    let i = 0

    let result = normalizeDataset(
      map(results, (r, name) => ({
        ...getPlotData({ plot: [aggregate], results: r, interval, masterDateSpan, factor })[0],
        name,
        label: name,
        color: getColor(i++)
      })),
      normalize
    )

    if (reorder) {
      result = result.sort((a, b) => {
        const _a = reorder.indexOf(a.name)
        const _b = reorder.indexOf(b.name)
        return _a > _b
          ? 1
          : _a < _b
            ? -1
            : 0
      }).map((r, i) => ({ ...r, color: getColor(i) }))
    }

    return {
      displayName: displayName || `${parameterName} grouped by ${groupBy}`,
      data: result
    }
  })))
)

export const getDocumentConfiguration = (includeSharedEvents = true) => {
  const requests = dealTypes.map(({ value: type }) => (
    requestEventConfiguration({ type }).pipe(
      rxMap(response => ({ [type]: response }))
    ))
  )
  const configurations = includeSharedEvents
    ? combineLatest([...requests, requestSharedEventsConfiguration()])
    : combineLatest([...requests])

  return configurations.pipe(rxMap(configurations => configurations.reduce((acc, curr) => ({
    ...acc, ...curr
  })), {}))
}

export const getComparableParameters = (deals, documentConfiguration) => {
  const allParams = deals
    .reduce(
      (acc, deal) => [
        ...acc,
        ...map(deal.params, param => ({
          ...param,
          dealId: deal.dealId,
          config: documentConfiguration.parameters[param.descriptor.name]
        }))
      ],
      []
    )
    .filter(x => !!x.config)
    .sort((a, b) => a.config.index - b.config.index)

  const paramsByName = groupBy(allParams, x => x.config.name)

  return reduce(
    paramsByName,
    (
      acc,
      group,
      key // group.length > 1
    ) => ({
      ...acc,
      [key]: {
        name: group[0].config.name,
        description: group[0].config.description,
        deals: group.reduce((a, c) => ({ ...a, [c.dealId]: c }), {})
      }
    }),
    {}
  )
}

export const getDealParameters = (deal, documentConfig) => {
  const paramsWithComments = map(deal.params, param => {
    const config = result(documentConfig, [
      'parameters',
      param.descriptor.name
    ])
    const comment = result(deal, [
      'comments',
      param.descriptor.name,
      'value'
    ])
    return param.extend({
      config,
      comment
    })
  })

  const filteredAndSorted = sortBy(
    filter(paramsWithComments, x => !!x.config),
    'config.index'
  )

  // using reduce instead of groupBy to persist order of params
  const grouped = reduce(
    filteredAndSorted,
    ({ groupRef, groups }, param) => {
      const { group } = param.config
      if (!Object.prototype.hasOwnProperty.call(groupRef, group)) {
        groupRef[group] = groups.push({ groupName: group, group: [] }) - 1
      }
      const groupIndex = groupRef[group]
      groups[groupIndex].group.push(param)
      return { groupRef, groups }
    },
    { groupRef: {}, groups: [] }
  )

  return grouped.groups
}

// Originally we tried using the `.utc` function provided by the dayjs utc plugin (which is extended by and therefore should be accessible
// via the objectSupport plugin) however this threw an error even though I believe we were using it exactly as the docs suggest
// So instead we'll use the `toISOString` function which is exposed by the main library
// See:
// - https://github.com/iamkun/dayjs/blob/v1.11.7/src/index.js#L366
// - https://day.js.org/docs/en/display/as-iso-string
export const getDealTimeline = (deal, documentConfig) => sortBy(
  deal.timeline.map(x => ({
    ...x,
    date: dayjs(x.value),
    sortValue: some(['Date of start', 'Date of first'], (el) => startsWith(result(documentConfig, ['parameters', x.descriptor.name, 'description']), el))
      ? dayjs(x.value).toISOString()
      : dayjs(x.value)
        .add({ minutes: 1 })
        .toISOString(),
    label: result(documentConfig, [
      'parameters',
      x.descriptor.name,
      'description'
    ]),
    document: deal.documents[x.descriptor.context.documentUuid]
  })),
  x => x.sortValue
)
